import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import Webform from '../components/Webform';
import { getGenerelleIndstillinger, FALLBACK_GENERELLT } from '../lib/cms';

export default function Kontakt() {
  const [searchParams] = useSearchParams();
  const defaultSubject = searchParams.get('emne') || '';
  const [info, setInfo] = useState(FALLBACK_GENERELLT);

  useEffect(() => {
    getGenerelleIndstillinger().then(data => {
      if (data) setInfo(data);
    });
  }, []);

  return (
    <div className="bg-[#090a0f] text-slate-100 min-h-screen pb-20">
      <Helmet>
        <title>Kontakt - Foreningen ØrBike</title>
        <meta
          name="description"
          content="Kontakt Foreningen ØrBike. Eventarrangører: Helle Holst Hansen og Jonas Reinholdt Albjerg. Telefon: +45 30 93 62 72, Email: info@orbike.dk, CVR: 45950433."
        />
        <meta property="og:title" content="Kontakt - Foreningen ØrBike" />
        <meta property="og:description" content="Har du spørgsmål eller ønsker du at blive partner til ØrBike? Kontakt os her." />
      </Helmet>

      {/* Hero Header */}
      <section className="relative pt-36 sm:pt-40 pb-16 sm:pb-20 bg-gradient-to-b from-[#181126] to-[#090a0f] border-b border-pink-500/20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-pink-500/20 text-pink-400 border border-pink-500/30 mb-4">
            Vi er her for at hjælpe
          </span>
          <h1 className="text-4xl sm:text-6xl font-black font-display text-white uppercase tracking-tight">
            Kontakt Foreningen ØrBike
          </h1>
          <p className="text-pink-300 font-display text-lg sm:text-2xl font-bold uppercase mt-2">
            Vil du mødes med os eller have svar på dine spørgsmål?
          </p>
        </div>
      </section>

      <section className="py-16 max-w-[1520px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Venstre Kolonne: Info & Fakta */}
          <div className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <h2 className="text-2xl sm:text-3xl font-black text-white font-display uppercase tracking-tight">
                Vi glæder os til at høre fra dig
              </h2>
              <p className="text-slate-300 text-sm leading-relaxed">
                Har du spørgsmål til ØrBike eventet eller lignende, så er du velkommen til at kontakte os.
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                Vi er drevet af frivillige og har derfor ikke en egentlig besøgsadresse, men vi mødes gerne med dig.
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                Vil du være sponsor eller samarbejdspartner, så er du velkommen til at kontakte os. Vi er altid på udkig efter nye samarbejdspartnere for at udvikle ØrBike.
              </p>
            </div>

            {/* Kontaktkort */}
            <div className="p-6 rounded-2xl bg-[#12141f] border border-slate-800 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-pink-500/20 text-pink-400 flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-location-dot" aria-hidden="true"></i>
                </div>
                <div>
                  <div className="text-xs uppercase font-bold text-slate-400 font-mono">Adresse</div>
                  <div className="text-sm font-semibold text-white mt-0.5">
                    {info.foreningsnavn || "Foreningen ØrBike"}<br />
                    {info.adresse || "C/O Jonas Reinholdt Albjerg\nDegnemosen 12, Tåsinge\n5700 Svendborg Danmark"}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 text-purple-400 flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-phone" aria-hidden="true"></i>
                </div>
                <div>
                  <div className="text-xs uppercase font-bold text-slate-400 font-mono">Telefon</div>
                  <a
                    href={`tel:${info.telefon ? info.telefon.replace(/[^0-9+]/g, '') : '4530936272'}`}
                    className="whitespace-nowrap text-sm font-bold text-pink-400 hover:text-pink-300 underline decoration-pink-500/40 mt-0.5 block"
                  >
                    {info.telefon || "+45 30 93 62 72"}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-pink-500/20 text-pink-400 flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-envelope" aria-hidden="true"></i>
                </div>
                <div>
                  <div className="text-xs uppercase font-bold text-slate-400 font-mono">Email</div>
                  <a
                    href={`mailto:${info.email || 'info@orbike.dk'}`}
                    className="whitespace-nowrap text-sm font-bold text-pink-400 hover:text-pink-300 underline decoration-pink-500/40 mt-0.5 block"
                  >
                    {info.email || "info@orbike.dk"}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 text-purple-400 flex items-center justify-center shrink-0">
                  <i className="fa-solid fa-id-card" aria-hidden="true"></i>
                </div>
                <div>
                  <div className="text-xs uppercase font-bold text-slate-400 font-mono">CVR Nummer</div>
                  <span className="whitespace-nowrap font-mono text-sm font-bold text-white mt-0.5 block">
                    {info.cvr || "45950433"}
                  </span>
                </div>
              </div>
            </div>

            {/* Eventsted info */}
            <div className="p-6 rounded-2xl bg-gradient-to-r from-pink-950/20 to-slate-900 border border-pink-500/20 text-xs text-slate-300 space-y-1">
              <strong className="text-white block font-display uppercase text-sm">Selve eventet afholdes i:</strong>
              <p>Ørbæk Midtpunkt, Langemosevej 5, 5853 Ørbæk</p>
            </div>
          </div>

          {/* Højre Kolonne: Webform */}
          <div className="lg:col-span-7">
            <Webform defaultSubject={defaultSubject} />
          </div>
        </div>
      </section>
    </div>
  );
}
